import { countUp, emphasizedDecelerate, linear, popScale, progressAt, slam } from 'modules/match-video/domain/scenes/kit';
import { FRAME_CENTER_X, SCORE_GAP } from './style';

/** Set A intro length: 2 s of build-up, 2 s to read (the mockup's intro loop). */
export const NIGHT_INTRO_SEC = 4;

/** The whole layer slowly pushes in (`pushIn`, 4 s linear). */
const PUSH_IN = 0.035;
/** Score slam (`.a-score`, `slam` .55 s from 1.3 s) and its count-up (from 1.45 s for .65 s). */
const SLAM_AT = 1.3;
const SLAM_SEC = 0.55;
const COUNT_AT = 1.45;
const COUNT_SEC = 0.65;

const dec = emphasizedDecelerate;

/** A fade/slide driven by `--dec` with `fill-mode: both`. */
const decAt = (t: number, delaySec: number, durationSec: number) => dec(progressAt(t, delaySec, durationSec));

export interface SlideState {
  /** Horizontal shift from the final place, px. */
  dx: number;
  alpha: number;
}

export interface NightIntroState {
  pushScale: number;
  bgAlpha: number;
  beam: {
    alpha: number;
    /** Drift across the band (along its own y axis), px. */
    shiftY: number;
    /** Left edge of the highlight along the 6000 px band, px from the band start. */
    glossX: number;
  };
  orgAlpha: number;
  champRise: number;
  roundScale: number;
  homeLogo: SlideState;
  awayLogo: SlideState;
  homeNameRise: number;
  awayNameRise: number;
  score: { scale: number; alpha: number; homeShown: number | null; awayShown: number | null };
  foot: { alpha: number; letterSpacing: number };
}

const countShown = (score: number | undefined, t: number): number | null =>
  score === undefined ? null : countUp(score, progressAt(t, COUNT_AT, COUNT_SEC));

/**
 * Every animated value of the set A intro at `t` seconds, as the mockup's CSS animations give it (`.a-*` elements,
 * delays and durations from their inline `--d` / `--t`). Pure — tests read it at key moments.
 */
export function nightIntroState(t: number, homeScore?: number, awayScore?: number): NightIntroState {
  const logo = (sign: 1 | -1): SlideState => {
    const p = decAt(t, 0.7, 0.8);
    return { dx: sign * 420 * (1 - p), alpha: p };
  };
  const linearAt = (delaySec: number, durationSec: number) => linear(progressAt(t, delaySec, durationSec));
  const scoreSlam = slam(t, SLAM_AT, SLAM_SEC);
  const foot = decAt(t, 1.8, 0.8);

  return {
    pushScale: 1 + PUSH_IN * linearAt(0, NIGHT_INTRO_SEC),
    bgAlpha: decAt(t, 0, 0.4),
    beam: {
      // `beam`: opacity 0 → 1 over the first 12 %, the band drifts −180 → 220 px; `beamSlide`: 600 → 3600 px.
      alpha: linearAt(0, 0.12 * NIGHT_INTRO_SEC),
      shiftY: -180 + 400 * linearAt(0, NIGHT_INTRO_SEC),
      glossX: 600 + 3000 * linearAt(0, NIGHT_INTRO_SEC),
    },
    orgAlpha: decAt(t, 0.3, 0.5),
    champRise: decAt(t, 0.2, 0.7),
    roundScale: popScale(t, 0.55, 0.5),
    homeLogo: logo(-1),
    awayLogo: logo(1),
    homeNameRise: decAt(t, 1.0, 0.6),
    awayNameRise: decAt(t, 1.05, 0.6),
    score: { ...scoreSlam, homeShown: countShown(homeScore, t), awayShown: countShown(awayScore, t) },
    foot: { alpha: foot, letterSpacing: 22 + (2 - 22) * foot },
  };
}

export interface ScoreColumns {
  /** Right edge of the home number. */
  homeRight: number;
  colonLeft: number;
  awayLeft: number;
}

/**
 * The score row `1fr auto 1fr`: the colon is centred on the frame whatever the numbers, the home number is pressed
 * against it from the left, the away one from the right. «10» is wider than «3», so centring the whole line would push
 * the colon aside — and it would shift while the score counts up.
 */
export function scoreColumns(colonWidth: number): ScoreColumns {
  const colonLeft = FRAME_CENTER_X - colonWidth / 2;
  return { homeRight: colonLeft - SCORE_GAP, colonLeft, awayLeft: colonLeft + colonWidth + SCORE_GAP };
}
